'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Navbar from '@components/Navbar';
import Footer from '@components/footer/Footer';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  Separator,
} from '@/components/ui';
import {
  BookOpen,
  Calendar,
  MessageSquare,
  Star,
} from 'lucide-react';

const DEFAULT_AVATAR = '/images/demo-profile.png';

const getHeroImageUrl = (imageUrl) => {
  if (!imageUrl) return imageUrl;
  if (imageUrl.includes('/card/') && imageUrl.includes('_250x200')) {
    return imageUrl
      .replace('/card/', '/hero/')
      .replace('_250x200', '_500xauto');
  }
  return imageUrl;
};

const getInitials = (name) =>
  (name || 'Reviewer')
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  const date =
    typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)
      ? new Date(`${dateString}T12:00:00.000Z`)
      : new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatAverageRating = (rating) => {
  const numericRating = Number(rating);
  if (!Number.isFinite(numericRating) || numericRating <= 0) {
    return '0.0';
  }
  return numericRating.toFixed(1);
};

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < Math.round(rating)
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-gray-200 text-gray-200'
          }`}
        />
      ))}
    </div>
  );
}

function normalizeProfileData(profileData = {}) {
  const reviewer = profileData.reviewer || profileData.reviewerData || {};
  const reviews = Array.isArray(profileData.reviews)
    ? profileData.reviews
    : Array.isArray(profileData.userReviews)
      ? profileData.userReviews
      : [];

  const reviewedClasses = Array.isArray(profileData.reviewedClasses)
    ? profileData.reviewedClasses
    : Array.isArray(profileData.classes)
      ? profileData.classes
      : Array.from(
          new Map(
            reviews
              .filter((review) => review.courseId || review.courseName || review.course?.id)
              .map((review) => {
                const courseId = review.courseId || review.course?.id || review.classId || null;
                const courseName =
                  review.courseName || review.course?.name || review.className || 'Untitled Course';
                const courseData = review.course || {};

                return [
                  `${courseId ?? courseName}`,
                  {
                    id: courseId,
                    name: courseName,
                    description: courseData.description || '',
                    imageUrl: courseData.imageUrl || null,
                  },
                ];
              })
          ).values()
        );

  const reviewCount =
    profileData.reviewCount ??
    profileData.reviewsCount ??
    profileData.totalReviews ??
    profileData.stats?.reviewCount ??
    reviewer.reviewCount ??
    reviews.length;

  const averageRating =
    profileData.averageRating ??
    profileData.avgRating ??
    profileData.stats?.averageRating ??
    reviewer.averageRating ??
    0;

  return {
    reviewer: {
      name: reviewer.name || profileData.name || 'Reviewer',
      description:
        reviewer.description ||
        reviewer.bio ||
        reviewer.headline ||
        profileData.description ||
        profileData.bio ||
        profileData.headline ||
        '',
      avatarUrl: reviewer.avatarUrl || profileData.avatarUrl || DEFAULT_AVATAR,
      memberSince:
        reviewer.memberSince ||
        profileData.memberSince ||
        reviewer.createdAt ||
        profileData.createdAt ||
        null,
    },
    reviewedClasses,
    reviews,
    reviewCount: Number(reviewCount) || 0,
    averageRating: Number(averageRating) || 0,
  };
}

export default function ReviewerProfilePageClient({ profileData }) {
  const { reviewer, reviewedClasses, reviews, reviewCount, averageRating } =
    normalizeProfileData(profileData);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const hasAvatar =
    reviewer.avatarUrl &&
    reviewer.avatarUrl !== DEFAULT_AVATAR &&
    !avatarLoadFailed;

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [reviewer.avatarUrl]);

  return (
    <div className="min-h-screen bg-gray-50">
      <header>
        <Navbar />
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <Card className="lg:sticky lg:top-8">
              <CardContent className="p-5 sm:p-6">
                <div className="text-center">
                  <div className="mx-auto mb-4 relative h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28 rounded-full overflow-hidden bg-blue-100 ring-4 ring-white shadow-sm">
                    {hasAvatar ? (
                      // Signed avatar URLs are short-lived and not a fit for next/image optimization.
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={reviewer.avatarUrl}
                        alt={reviewer.name}
                        className="h-full w-full object-cover object-center"
                        onError={() => setAvatarLoadFailed(true)}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-blue-100 text-blue-700 text-lg font-semibold">
                        {getInitials(reviewer.name)}
                      </div>
                    )}
                  </div>

                  <h1 className="text-xl font-bold text-gray-900 mb-2">
                    {reviewer.name}
                  </h1>

                  {reviewer.description ? (
                    <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                      {reviewer.description}
                    </p>
                  ) : (
                    <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                      This reviewer has shared course feedback with the PenguinLearn
                      community.
                    </p>
                  )}

                  <Separator className="mb-6" />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span className="text-sm">Member since</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {formatDate(reviewer.memberSince)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center text-gray-600">
                        <MessageSquare className="h-4 w-4 mr-2" />
                        <span className="text-sm">Reviews written</span>
                      </div>
                      <Badge variant="secondary">{reviewCount}</Badge>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center text-gray-600">
                        <BookOpen className="h-4 w-4 mr-2" />
                        <span className="text-sm">Reviewed classes</span>
                      </div>
                      <Badge variant="secondary">{reviewedClasses.length}</Badge>
                    </div>

                    <div className="rounded-lg border border-yellow-100 bg-yellow-50 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-left">
                          <p className="text-sm font-medium text-gray-900">
                            Average rating
                          </p>
                          <p className="text-xs text-gray-600">
                            Across published reviews
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="flex justify-end mb-1">
                            <StarRating rating={averageRating} />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            {formatAverageRating(averageRating)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-3 space-y-8">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-green-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Reviewed Classes
                  </h2>
                </div>
              </CardHeader>
              <CardContent>
                {reviewedClasses.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No reviewed classes are available yet.
                  </p>
                ) : (
                  <div className="space-y-6">
                    {reviewedClasses.map((course, index) => {
                      const courseHref =
                        course.href ||
                        course.courseHref ||
                        (course.id ? `/classes/${course.id}` : null);

                      const cardContent = (
                        <div className="flex flex-col md:flex-row gap-4 p-4 rounded-lg border border-gray-200 hover:border-green-300 hover:shadow-md transition-all">
                          {/* Course images can come from dynamic storage/public URLs. */}
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={
                              getHeroImageUrl(course.imageUrl) || '/images/class.png'
                            }
                            alt={course.name || 'Reviewed course'}
                            className="w-full md:w-48 h-32 object-contain rounded-lg bg-gray-50"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-2">
                              {course.name || 'Untitled Course'}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                              {course.description || 'No course description available.'}
                            </p>
                          </div>
                        </div>
                      );

                      if (!courseHref) {
                        return <div key={`${course.name}-${index}`}>{cardContent}</div>;
                      }

                      return (
                        <Link key={`${courseHref}-${index}`} href={courseHref}>
                          {cardContent}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Reviews by {reviewer.name}
                  </h2>
                </div>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No reviews are available yet.
                  </p>
                ) : (
                  <div className="space-y-6">
                    {reviews.map((review) => {
                      const courseHref =
                        review.courseHref ||
                        review.classHref ||
                        (review.courseId ? `/classes/${review.courseId}` : null);
                      const courseName =
                        review.courseName ||
                        review.course?.name ||
                        review.className ||
                        'Reviewed course';

                      return (
                        <div
                          key={review.id}
                          className="p-4 rounded-lg bg-gray-50 border border-gray-200"
                        >
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <div>
                              {courseHref ? (
                                <Link
                                  href={courseHref}
                                  className="font-medium text-gray-900 hover:underline"
                                >
                                  {courseName}
                                </Link>
                              ) : (
                                <h3 className="font-medium text-gray-900">
                                  {courseName}
                                </h3>
                              )}
                              <div className="mt-1">
                                <StarRating rating={review.rating || 0} />
                              </div>
                            </div>
                            <span className="text-sm text-gray-500">
                              {formatDate(review.postedAt || review.createdAt)}
                            </span>
                          </div>
                          <p className="text-gray-700 leading-relaxed">
                            {review.content}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
